import * as yup from 'yup';
import { useForm } from 'react-form-state-manager';
import { useTranslation } from 'react-i18next';

import { GetTicketNumberSequenceQuery as GetTicketNumberSequence } from '__generated__/graphql';
import { encodeUrl, getServerErrors } from '../../../common/helpers';
import UI from '../../../common/UI';
import useEditTicketNumberSequenceMutation from './useEditTicketNumberSequenceMutation';
import useIntegerInput from '../../../common/useIntegerInput';
import useNavigationPrompt from '../../../common/useNavigationPrompt';
import useProject from '../../useProject';
import useValidation from '../../../common/useValidation';

type TicketNumberSequence = GetTicketNumberSequence['project']['ticket_number_sequence'];

const editTicketNumberSequenceInfoSchema = yup.object().shape({
  title: yup.string().ensure().required(),
  start_number: yup.number().min(1).required(),
});

interface EditTicketNumberSequenceInfoFormProps {
  sequence: TicketNumberSequence;
}

const EditTicketNumberSequenceInfoForm = ({ sequence }: EditTicketNumberSequenceInfoFormProps) => {
  const { t } = useTranslation();
  const project = useProject();
  const integerInput = useIntegerInput();

  const getValues = (sequence: TicketNumberSequence) => ({
    title: sequence.title,
    start_number: sequence.start_number,
    prefix: sequence.prefix,
    suffix: sequence.suffix,
    start_time: sequence.start_time,
    corral_name: sequence.corral_name,
    capacity: sequence.capacity,
    locked: sequence.locked,
  });

  const form = useForm({
    values: getValues(sequence),
  });

  useNavigationPrompt(form.changed());

  const [editTicketNumberSequence, { error, loading }] = useEditTicketNumberSequenceMutation({
    input: {
      id: sequence.id,
      info: form.values,
    },
    onSuccess: (sequence) => {
      form.reset(getValues(sequence));
    },
  });

  const { valid, errors } = useValidation({
    schema: editTicketNumberSequenceInfoSchema,
    values: form.values,
    externalErrors: getServerErrors(error),
  });

  return (
    <UI.Form onSubmit={() => editTicketNumberSequence()}>
      <UI.FormGrid>
        {sequence.assigned_ticket_number_count > 0 && (
          <UI.Info>
            {t('edit_sequence_ticket_numbers_assigned_description')}
          </UI.Info>
        )}
        <UI.ServerErrors error={error} />
        <UI.Div>
          <UI.Legend sc={{ mb: 1 }}>{t('ticket_number_sequence')}</UI.Legend>
          <UI.InputDescription>
            {t('ticket_number_sequence_info_description')}
          </UI.InputDescription>
        </UI.Div>
        <UI.InputGroup sc={{ valid: !errors.title, touched: form.touched.title }}>
          <UI.InputLabel htmlFor="SequenceTitle">
            {t('title')}
            {' '}
            <UI.RequiredMark />
          </UI.InputLabel>
          <UI.InputDescription>
            {t('not_visible_to_participants')}
          </UI.InputDescription>
          <UI.DebouncedInput
            {...form.text('title')}
            maxLength={255}
            placeholder={t('ticket_number_sequence_title_placeholder')}
            autoFocus={!sequence}
            id="SequenceTitle"
          />
          <UI.ErrorMessages attribute={t('title')} errors={errors.title} />
        </UI.InputGroup>
        <UI.FormGrid sc={{ columns: ['1fr', '1fr 1fr 1fr'] }}>
          <UI.InputGroup sc={{ valid: !errors.prefix, touched: form.touched.prefix }}>
            <UI.InputLabel htmlFor="SequencePrefix">
              {t('prefix')}
            </UI.InputLabel>
            <UI.DebouncedInput
              {...form.text('prefix')}
              maxLength={3}
              id="SequencePrefix"
            />
            <UI.ErrorMessages attribute={t('prefix')} errors={errors.prefix} />
          </UI.InputGroup>
          <UI.InputGroup sc={{ valid: !errors.start_number, touched: form.touched.start_number }}>
            <UI.InputLabel htmlFor="SequenceStartNumber">
              {t('start_number')}
              {' '}
              <UI.RequiredMark />
            </UI.InputLabel>
            <UI.DebouncedInput
              {...form.text('start_number', {
                inputHandler: integerInput(),
              })}
              maxLength={8}
              format={integerInput().format}
              inputMode="numeric"
              match={/[\d]/g}
              id="SequenceStartNumber"
            />
            <UI.ErrorMessages attribute={t('start_number')} errors={errors.start_number} />
          </UI.InputGroup>
          <UI.InputGroup sc={{ valid: !errors.suffix, touched: form.touched.suffix }}>
            <UI.InputLabel htmlFor="SequenceSuffix">
              {t('suffix')}
            </UI.InputLabel>
            <UI.DebouncedInput
              {...form.text('suffix')}
              maxLength={3}
              id="SequenceSuffix"
            />
            <UI.ErrorMessages attribute={t('suffix')} errors={errors.suffix} />
          </UI.InputGroup>
        </UI.FormGrid>

        <UI.FormGrid sc={{ columns: ['1fr', '1fr 1fr 1fr'] }}>
          <UI.InputGroup sc={{ valid: !errors.corral_name, touched: form.touched.corral_name }}>
            <UI.InputLabel htmlFor="SequenceCorralName">
              {t('corral_name')}
            </UI.InputLabel>
            <UI.DebouncedInput {...form.text('corral_name')} id="SequenceCorralName" />
            <UI.ErrorMessages attribute={t('corral_name')} errors={errors.corral_name} />
          </UI.InputGroup>
          <UI.InputGroup sc={{ valid: !errors.start_time, touched: form.touched.start_time }}>
            <UI.InputLabel htmlFor="SequenceStartTime">
              {t('start_time')}
            </UI.InputLabel>
            <UI.TimePicker
              selected={form.get('start_time')}
              onChange={(value) => form.set('start_time', value)}
              timeIntervals={5}
              id="SequenceStartTime"
            />
            <UI.ErrorMessages attribute={t('start_time')} errors={errors.start_time} />
          </UI.InputGroup>
          <UI.InputGroup sc={{ valid: !errors.capacity, touched: form.touched.capacity }}>
            <UI.InputLabel htmlFor="SequenceCapacity">
              {t('capacity')}
            </UI.InputLabel>
            <UI.DebouncedInput
              {...form.text('capacity', {
                inputHandler: integerInput(),
              })}
              format={integerInput().format}
              maxLength={8}
              inputMode="numeric"
              match={/[\d]/g}
              placeholder="∞"
              id="SequenceCapacity"
            />
            <UI.ErrorMessages attribute={t('capacity')} errors={errors.capacity} />
          </UI.InputGroup>
        </UI.FormGrid>

        <UI.InputGroup>
          <UI.InputLabel htmlFor="SequenceLocked">
            {t('lock_registrations')}
          </UI.InputLabel>
          <UI.InputDescription>
            {t('lock_registrations_description')}
          </UI.InputDescription>
          <UI.Checkbox {...form.checkbox('locked')} id="SequenceLocked">
            {t('lock_registrations')}
          </UI.Checkbox>
        </UI.InputGroup>

        <UI.HR />

        <UI.FormButtons
          form={form}
          submitLabel={t('save_ticket_number_sequence')}
          closeTo={`/${project.id}/participants/bib-numbers?${encodeUrl({ event: sequence.event.id })}`}
          loading={loading}
          valid={valid}
        />
      </UI.FormGrid>
    </UI.Form>
  );
};

export default EditTicketNumberSequenceInfoForm;
